// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { createTypedReducerWithImmer } from "@insite/client-framework/Common/CreateTypedReducer";
import { GetShipTosApiParameter } from "@insite/client-framework/Services/CustomersService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { AnyAction, reducers } from "@insite/client-framework/Store/Reducers";
import { CreditCardBillingAddressDto } from "@insite/client-framework/Types/ApiModels";
import { Draft } from "immer";
import merge from "lodash/merge";

interface ShippingPageState {
    shipPartial?: string;
    lockShipTo?: boolean;
    timeslot?: string;
    shipTosParams?: GetShipTosApiParameter;
    loading?: boolean;
    isNewAddress?: boolean;
}

interface PaymentPageState {
    poNumber?: string;
    jobName?: string;
    contactName?: string;
    contactPhone?: string;
    lastFour?: string;
    cardType?: string;
    isCreditCard?: boolean;
    paymentMethod?: string;
    expirationDate?: string;
    useNewCreditCard?: boolean;
    savedPaymentDescription?: string;
    isSavedPayment?: boolean;
    creditCardBillingAddress?: CreditCardBillingAddressDto;
}

export interface XCheckoutStateType {
    shipping: ShippingPageState;
    payment: PaymentPageState;
}

export const initialCheckoutState: XCheckoutStateType = {
    payment: {
        useNewCreditCard: true,
    },
    shipping: {
        shipPartial: "true",
        timeslot: "",
        shipTosParams: {},
        loading: false,
        isNewAddress: false,
    },
};

const reducer = {
    "xCheckout/Update": (draft: Draft<XCheckoutStateType>, action: { data: Partial<XCheckoutStateType> }) => {
        draft = merge(draft, action.data);
    },
    "xCheckout/Reset": (draft: Draft<XCheckoutStateType>, action: { path?: keyof XCheckoutStateType }) => {
        if (!action.path) {
            draft.payment = {
                useNewCreditCard: true,
            };
            draft.shipping = {
                shipPartial: "true",
                timeslot: "",
                shipTosParams: {},
                loading: false
            };
            return;
        }
        draft[action.path] = initialCheckoutState[action.path];
    },
};

const xCheckout = createTypedReducerWithImmer(initialCheckoutState, reducer);

(reducers as any).xCheckout = xCheckout;

export type xCheckoutState = {
    xCheckout: XCheckoutStateType;
} & ApplicationState;

type xCheckoutReducers = {
    [key: string]: typeof xCheckout;
};

export type xCheckoutActions = Parameters<xCheckoutReducers[keyof xCheckoutReducers]>[1] | AnyAction;
